import { useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { useAuth } from "@/hooks/useAuth";
import { useGetAccountByIdQuery } from "@/redux/api/accounts.rtkq";
import { Card } from "@/components/ui/card";
import { ProfileForm } from "@/components/profile/ProfileForm";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";
import { Building2 } from "lucide-react";

const AccountPage = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const accountId = auth.getAccountId();
  const [dismissedEnforcement, setDismissedEnforcement] = useState(false);

  const {
    data: account,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useGetAccountByIdQuery({ accountId: accountId! }, { skip: !accountId });

  const initialValues = useMemo(
    () => ({
      phoneNumber: account?.accountInformation?.phoneNumber || "",
      businessName: account?.accountInformation?.businessName || "",
      businessNumber: account?.accountInformation?.businessNumber || "",
      city: account?.accountInformation?.city || "",
    }),
    [
      account?.accountInformation?.phoneNumber,
      account?.accountInformation?.businessName,
      account?.accountInformation?.businessNumber,
      account?.accountInformation?.city,
    ],
  );

  const showEnforcementBanner =
    !dismissedEnforcement &&
    (location.state as { enforced?: boolean } | null)?.enforced === true &&
    account?.accountInformation?.isProfileComplete !== true;

  const handleSuccess = () => {
    toast.success("הפרופיל עודכן בהצלחה");
    setDismissedEnforcement(true);
    const state = (location.state as { enforced?: boolean; from?: string } | null) ?? {};
    const from = state.from;
    // Drop enforced redirect state so the banner does not stick after save.
    if (from && from !== "/account") {
      navigate(from, { replace: true });
      return;
    }
    if (state.enforced) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  };

  const handleError = (error: unknown) => {
    console.error("Error updating profile:", error);
    toast.error(error instanceof Error ? error.message : "שגיאה בעדכון הפרופיל");
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Spinner className="size-8" />
          <p className="text-muted-foreground">טוען...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <div className="space-y-4 text-center">
          <p className="text-lg font-semibold text-destructive">שגיאה בטעינת נתוני החשבון</p>
          <p className="text-muted-foreground">
            {error && typeof error === "object" && "message" in error
              ? String(error.message)
              : "אירעה שגיאה לא צפויה"}
          </p>
        </div>
      </div>
    );
  }

  if (isSuccess && account) {
    return (
      <div className="container mx-auto p-6" dir="rtl">
        <div className="mx-auto max-w-2xl space-y-6">
          <div className="space-y-2 text-center">
            <div className="flex justify-center">
              <div className="rounded-full bg-chen-100 p-3">
                <Building2 className="h-8 w-8 text-chen-900" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-chen-900">הגדרות חשבון</h1>
            <p className="text-muted-foreground">נהלו את פרטי הפרופיל העסקי שלכם</p>
          </div>

          {showEnforcementBanner && (
            <div className="rounded-lg border border-chen-900 bg-chen-100 p-6 text-center">
              <h2 className="text-lg font-bold text-chen-900">השלמת הפרופיל נדרשת</h2>
              <p className="mt-2 text-chen-900">
                על מנת להמשיך להשתמש במערכת, אנא השלימו את פרטי החשבון
              </p>
            </div>
          )}

          <Card className="p-8">
            <ProfileForm
              initialValues={initialValues}
              onSuccess={handleSuccess}
              onError={handleError}
              submitButtonText="שמור שינויים"
            />
          </Card>
        </div>
      </div>
    );
  }

  return null;
};

export default AccountPage;
